import { faArrowUpRightFromSquare } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observer } from 'mobx-react-lite';
import type { JSX, ReactNode } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type {
  IMailrailDripStep,
  IMailrailSingleSendMailing,
  MailrailBaseEmailCampaign,
} from '@feathr/blackbox';
import {
  Button,
  CardV2 as Card,
  CopyToClipboardButton,
  EmailPreview,
  Label,
  Time,
  useEmailPreview,
} from '@feathr/components';
import { useStore, useUser } from '@feathr/extender/state';
import { TimeFormat } from '@feathr/hooks';

import * as styles from './MailrailEmailDetails.css';

interface IProps {
  campaign: MailrailBaseEmailCampaign;
  /** A single send's embedded mailing, such as an A/B variant, shown instead of the campaign's own email. */
  mailing?: IMailrailSingleSendMailing;
  /** Rendered in the card's top-right corner, e.g. A/B version labels. */
  pills?: ReactNode;
  step?: IMailrailDripStep;
}

function MailrailEmailDetails({ campaign, mailing, pills, step }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const { Templates } = useStore();
  const user = useUser();

  const userTimezone = user.get('timezone');
  const dateStart = campaign.get('date_start');
  const embeddedMailing = step ?? mailing;
  const mailingId = step?.step_id ?? mailing?.mailing_id;
  const templateId = embeddedMailing ? embeddedMailing.template_id : campaign.get('template_id');
  const fromAddress =
    (embeddedMailing ? embeddedMailing.from_address : campaign.get('from_address')) ?? t('N/A');
  const fromName =
    (embeddedMailing ? embeddedMailing.from_name : campaign.get('from_name')) ?? t('N/A');
  const subject = templateId ? Templates.get(templateId)?.get('subject') : undefined;

  const previewUrl = campaign.getTemplatePreviewUrl({ templateId, mailingId });
  const { fetchFailed: previewFetchFailed, isLoading: isPreviewLoading } = useEmailPreview({
    url: previewUrl,
    t,
  });

  const previewTitle = t('{{title}} Preview', { title: subject });

  return (
    <Card width={'full'}>
      <Card.Content contentClassName={styles.content}>
        {previewUrl && <EmailPreview title={previewTitle} url={previewUrl} />}

        <div className={styles.details}>
          <span className={styles.detail}>
            <Label className={styles.label}>{t('Subject')}</Label>
            {subject}
          </span>

          <span className={styles.detail}>
            <Label className={styles.label}>{t('From name')}</Label>
            {fromName}
          </span>

          <span className={styles.detail}>
            <Label className={styles.label}>
              {campaign.isMailrailSingleSend ? t('Sent date') : t('Start date')}
            </Label>
            <Time
              format={TimeFormat.pickerDateTimeZone}
              timestamp={dateStart}
              timezone={userTimezone}
            />
          </span>

          <span className={styles.detail}>
            <Label className={styles.label}>{t('From address')}</Label>
            {fromAddress}
          </span>

          {previewUrl && (
            <div className={styles.buttonWrapper}>
              <Button
                disabled={isPreviewLoading || previewFetchFailed}
                href={previewUrl}
                prefix={<FontAwesomeIcon icon={faArrowUpRightFromSquare} />}
                target={'_blank'}
                tooltip={previewFetchFailed && t('This email preview is unavailable')}
                tooltipPosition={'bottom'}
                type={'secondary'}
              >
                {t('View email')}
              </Button>
              <CopyToClipboardButton
                disabled={isPreviewLoading || previewFetchFailed}
                successMessage={t('Public link copied to clipboard')}
                t={t}
                text={previewUrl}
              >
                {t('Copy public link')}
              </CopyToClipboardButton>
            </div>
          )}
        </div>

        {pills && <div className={styles.pills}>{pills}</div>}
      </Card.Content>
    </Card>
  );
}

export default observer(MailrailEmailDetails);
